// @ts-nocheck
/**
 * Call Scripts list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + DataTable + DropdownMenu row actions + ConfirmDialog + shadcn Dialog
 * for View details).
 * Preserved: is_default star toggle (set-default endpoint), multi-delete,
 * search across title/short_description/description, ACL gates,
 * CKEditor HTML in the View modal via dangerouslySetInnerHTML.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Eye,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CALL_SCRIPT_LIST,
  API_CALL_SCRIPT_DELETE,
  API_CALL_SCRIPT_SET_DEFAULT,
} from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { hasPermission } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (dateStr: any) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d as any)) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CallScriptList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [dataList, setDataList] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loggedInUserRole, setLoggedInUserRole] = useState('');
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [defaultLoadingId, setDefaultLoadingId] = useState<any>(null);
  const [detailScript, setDetailScript] = useState<any>(null);
  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    setLoggedInUserRole(user?.role || '');
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    fetchData();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchData = () => {
    getApiCall(API_CALL_SCRIPT_LIST)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data ?? [];
        const updated = list.map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
        }));
        setDataList(updated);
        setOriginalData(updated);
      })
      .catch((err: any) =>
        toast.error(friendlyApiError(err) || 'Failed to fetch scripts'),
      )
      .finally(() => setLoading(false));
  };

  // Name/description filtering is handled by the DataTable's built-in
  // `globalFilter` via `searchPlaceholder`, so no manual filter here.
  useEffect(() => {
    setDataList(originalData);
  }, [originalData]);

  const confirmDelete = async () => {
    setDeleting(true);
    const deleteOne = (id: any) => postApiCall(API_CALL_SCRIPT_DELETE, { id });
    try {
      if (pendingBulk) {
        await Promise.all(selectedItems.map(deleteOne));
        toast.success('Selected scripts deleted');
        setSelectedItems([]);
        setPendingBulk(false);
      } else if (pendingDelete) {
        await deleteOne(pendingDelete.id);
        toast.success('Script deleted');
        setPendingDelete(null);
      }
      fetchData();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectAll = (checked: any) =>
    setSelectedItems(checked ? dataList.map((item: any) => item.id) : []);

  const handleSetDefault = (rowData: any) => {
    if (!rowData?.id) return;
    const remove = Boolean(rowData?.is_default);
    setDefaultLoadingId(rowData.id);
    postApiCall(API_CALL_SCRIPT_SET_DEFAULT.replace(':id', rowData.id), {
      remove_default: remove,
    })
      .then(() => {
        toast.success(remove ? 'Default script removed' : 'Default script updated');
        fetchData();
      })
      .catch((err: any) =>
        toast.error(
          friendlyApiError(err) ||
            (remove ? 'Unable to remove default script' : 'Unable to set default script'),
        ),
      )
      .finally(() => setDefaultLoadingId(null));
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.CALL_SCRIPT,
    'read',
  );
  const canWriteRole =
    loggedInUserRole === USER_ROLES.SUPERADMIN ||
    loggedInUserRole === USER_ROLES.CALL_CENTRE_ADMIN;
  const canAdd =
    canWriteRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_SCRIPT, 'add');
  const canEdit =
    canWriteRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_SCRIPT, 'edit');
  const canDelete =
    canWriteRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CALL_SCRIPT, 'delete');
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={selectedItems.length === dataList.length && dataList.length > 0}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedItems.includes(row.original.id)}
            onCheckedChange={() =>
              setSelectedItems((prev: any[]) =>
                prev.includes(row.original.id)
                  ? prev.filter((i: any) => i !== row.original.id)
                  : [...prev, row.original.id],
              )
            }
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        size: 240,
        cell: ({ row }) => {
          const nm = row.original.title || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              {canEdit ? (
                <Link
                  to={`/call-scripts/edit/${row.original.id}`}
                  className="font-medium text-foreground hover:underline"
                >
                  {nm}
                </Link>
              ) : (
                <span className="font-medium text-foreground">{nm}</span>
              )}
            </div>
          );
        },
      },
      {
        id: 'default',
        header: 'Default',
        size: 110,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.is_default ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
              <Star className="h-3 w-3 fill-current" />
              Default
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'short_description',
        header: 'Short Description',
        cell: ({ row }) => (
          <span className="line-clamp-2 text-xs text-muted-foreground">
            {row.original.short_description || '—'}
          </span>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        size: 130,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.description ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDetailScript(row.original)}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 140,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const isSetting = defaultLoadingId === rowData.id;
          const canRowEdit = canEdit;
          const canRowDelete = canDelete && selectedItems.length === 0;
          return (
            <div className="flex items-center gap-1">
              {canRowEdit && (
                <button
                  type="button"
                  title={
                    rowData.is_default ? 'Default script — click to remove' : 'Set as default'
                  }
                  onClick={() => handleSetDefault(rowData)}
                  disabled={isSetting}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-60 ${
                    rowData.is_default
                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
                  }`}
                >
                  {isSetting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Star
                      className="h-3.5 w-3.5"
                      fill={rowData.is_default ? 'currentColor' : 'none'}
                    />
                  )}
                </button>
              )}
              {canRowEdit && (
                <button
                  type="button"
                  title={`Edit ${rowData.title}`}
                  onClick={() => navigate(`/call-scripts/edit/${rowData.id}`)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canRowDelete && (
                <button
                  type="button"
                  title={`Delete ${rowData.title}`}
                  onClick={() => setPendingDelete(rowData)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        },
      });
    }
    return cols;
  }, [
    selectedItems,
    dataList,
    canEdit,
    canDelete,
    loggedInUserRole,
    defaultLoadingId,
    navigate,
    showActions,
  ]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Call Scripts" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  const defaultScript = originalData.find((s: any) => s.is_default);
  const totalScripts = originalData.length;

  if (loading && originalData.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Call Scripts"
        description="Rich-text scripts agents can read on live calls. One default script per company."
        actions={
          <>
            {canDelete && selectedItems.length > 0 && (
              <Button variant="destructive" onClick={() => setPendingBulk(true)}>
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedItems.length})
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.CALL_SCRIPT.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Script
              </Button>
            )}
          </>
        }
      />

      {/* Stat strip — matches Country Codes / Trunks: rounded-full pill,
          circular icon well, neu-raised shadow. */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FileText className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total scripts</span>
          <span className="font-semibold text-foreground tabular-nums">{totalScripts}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-amber-900/60 dark:bg-amber-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
            <Star className="h-3.5 w-3.5 fill-current" />
          </span>
          <span className="text-amber-700 dark:text-amber-300">Default</span>
          <span className="max-w-[220px] truncate font-semibold text-amber-900 dark:text-amber-100">
            {defaultScript?.title || '—'}
          </span>
        </div>
      </div>

      <DataTable
        tableId="call-scripts:list:v1"
        columns={columns}
        data={dataList}
        isLoading={loading}
        searchPlaceholder="Search scripts by title or description…"
        emptyTitle="No scripts yet"
        emptyDescription="Create a script to give agents a live-call reference."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected scripts?' : 'Delete this script?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">{selectedItems.length}</span>{' '}
              script(s) will be permanently deleted.
            </>
          ) : (
            <>
              Script{' '}
              <span className="font-medium text-foreground">{pendingDelete?.title}</span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />

      <Dialog open={!!detailScript} onOpenChange={(o) => !o && setDetailScript(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detailScript?.title || 'Script details'}</DialogTitle>
          </DialogHeader>
          {detailScript?.short_description && (
            <p className="text-sm text-muted-foreground">
              {detailScript.short_description}
            </p>
          )}
          {detailScript?.description ? (
            <div
              className="ck-content prose prose-sm max-w-none break-words leading-relaxed"
              dangerouslySetInnerHTML={{ __html: detailScript.description }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No description available.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailScript(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default CallScriptList;
